import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { findConcept } from "@/data/stoicismConcepts";
import { CONCEPT_I18N, CONCEPT_LABELS } from "@/data/stoicismConceptsI18n";
import { CARD_NUM_LABEL, pickLang } from "@/data/stoicosCards";
import { useT } from "@/i18n/LanguageContext";

const Sect = ({ label, children }) => (
  <section className="mt-14">
    <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">{label}</h2>
    {children}
  </section>
);

export default function ConceptPage() {
  const { slug } = useParams();
  const { lang } = useT();
  const base = findConcept(slug);

  /* Localised rendering: NL/DE/FR/ES override every visible field; SEO,
     canonical, prerender and JSON-LD stay EN (base) by architecture. */
  const tr = (base && lang !== "en" && CONCEPT_I18N[slug]?.[lang]) || null;
  const c = base ? { ...base, ...(tr || {}) } : null;
  const labels = CONCEPT_LABELS[lang] || CONCEPT_LABELS.en;
  const cardLabel = c ? `${pickLang(CARD_NUM_LABEL, lang)} ${c.cardNum}` : "";

  useSEO({
    title: base ? (lang === "en" ? base.metaTitle : `${c.title} | StoicOs.ai`) : "Stoicism | StoicOs.ai",
    description: base ? base.metaDescription : "",
    canonical: base ? `https://stoicos.ai/stoicism/${base.slug}` : "https://stoicos.ai/stoic-training-cards",
    og: base ? { title: base.metaTitle, description: base.metaDescription, image: `https://stoicos.ai/cards/${base.image}`, type: "article" } : undefined,
    jsonLd: base ? [
      webPage({ name: base.title, description: base.metaDescription, url: `/stoicism/${base.slug}` }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "StoicOs Visual Library", url: "/stoic-training-cards" },
        { name: base.title, url: `/stoicism/${base.slug}` },
      ]),
      ...(base.faq.length ? [{
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: base.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }] : []),
    ] : [],
  });

  if (!c) return <Navigate to="/stoic-training-cards" replace />;
  const related = c.related
    .map((slug2) => {
      const rb = findConcept(slug2);
      if (!rb) return null;
      const rt = lang !== "en" ? CONCEPT_I18N[slug2]?.[lang]?.title : null;
      return { slug: rb.slug, title: rt || rb.title };
    })
    .filter(Boolean);

  return (
    <main data-testid="concept-page" className="relative pt-32 md:pt-40 pb-32">
      <article className="max-w-3xl mx-auto px-6 md:px-10">
        <Link to="/stoic-training-cards" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-12" data-testid="concept-back">
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs Visual Library
        </Link>

        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.concept} · {cardLabel}</p>
        <h1 data-testid="concept-title" className="font-serif text-4xl sm:text-5xl text-bone tracking-tight leading-[1.08]">{c.title}</h1>
        <p className="mt-8 text-bone/90 font-reading text-lg md:text-xl leading-[1.85]" data-testid="concept-answer">{c.answer}</p>

        <figure className="mt-12">
          <img src={`/cards/${c.image}`} alt={c.alt} width={c.imgW} height={c.imgH} loading="eager" className="w-full h-auto border border-white/10" />
          <figcaption className="mt-3 text-[11px] tracking-[0.18em] uppercase text-fog/60">{cardLabel} — {c.title}</figcaption>
        </figure>

        <Sect label={labels.definition}>
          <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{c.definition}</p>
        </Sect>
        <Sect label={labels.history}>
          <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{c.history}</p>
        </Sect>
        <Sect label={labels.means}>
          <ul className="space-y-3">{c.means.map((m, i) => (
            <li key={i} className="text-bone/85 font-reading text-base md:text-lg leading-[1.8] pl-5 border-l border-gold/40">{m}</li>
          ))}</ul>
        </Sect>
        <Sect label={labels.notMeans}>
          <ul className="space-y-3">{c.notMeans.map((m, i) => (
            <li key={i} className="text-bone/85 font-reading text-base md:text-lg leading-[1.8] pl-5 border-l border-white/15">{m}</li>
          ))}</ul>
        </Sect>
        <Sect label={labels.examples}>
          <ul className="space-y-3">{c.examples.map((m, i) => (
            <li key={i} className="text-bone/80 font-reading text-base md:text-lg leading-[1.8]">— {m}</li>
          ))}</ul>
        </Sect>
        <Sect label={`${labels.practice}: ${c.exercise.title}`}>
          <ol className="space-y-3 list-decimal list-inside">{c.exercise.steps.map((s, i) => (
            <li key={i} className="text-bone/85 font-reading text-base md:text-lg leading-[1.8]">{s}</li>
          ))}</ol>
        </Sect>
        <Sect label={labels.relation}>
          <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{c.relation}</p>
        </Sect>
        <Sect label={labels.deeper}>
          <ul className="space-y-2">{c.links.map((l, i) => (
            <li key={i}><Link to={l.to} className="text-gold hover:underline font-reading text-base md:text-lg" data-testid={`concept-link-${i}`}>{l.label} →</Link></li>
          ))}</ul>
        </Sect>
        {c.faq.length > 0 && (
          <Sect label={labels.faq}>
            <div className="space-y-8">{c.faq.map((f, i) => (
              <div key={i}>
                <h3 className="font-serif text-xl text-bone mb-2">{f.q}</h3>
                <p className="text-bone/80 font-reading text-base leading-[1.8]">{f.a}</p>
              </div>
            ))}</div>
          </Sect>
        )}
        <Sect label={labels.sources}>
          <ul className="space-y-1.5">{base.sources.map((s, i) => (
            <li key={i} className="text-fog/75 font-reading text-sm leading-relaxed">{s}</li>
          ))}</ul>
        </Sect>
        {related.length > 0 && (
          <Sect label={labels.related}>
            <div className="flex flex-wrap gap-3">{related.map((r) => (
              <Link key={r.slug} to={`/stoicism/${r.slug}`} className="px-4 py-2 text-[11px] tracking-[0.2em] uppercase border border-gold/30 text-gold/90 hover:bg-gold/10 transition-colors" data-testid={`concept-related-${r.slug}`}>{r.title}</Link>
            ))}</div>
          </Sect>
        )}
      </article>
    </main>
  );
}
